/**
 * Maps the subscription stored in Dexie Cloud (`user.data.subscription`) to the client's
 * `CurrentSubscription`.
 *
 * The stored record is a data contract with v1 and the server (`server/src/types/subscription.ts`):
 * it mixes snake_case (`interval_count`, `payment_error`, `price_id`, ...) with the camelCase of
 * `subscriptionProviderID`, `subscriptionProvider` and `isProduction`. The client type is camelCase, so a plain cast
 * left `intervalCount`, `paymentError` and `priceId` undefined for every real user (a two-year
 * plan showed as Yearly, a failed payment never showed). The stored names are read first; the
 * client's camelCase names stay as a fallback for rows written by older client code.
 */

import {
  PaymentPlan,
  PaymentProvider,
  PaymentTerm,
  type CurrentSubscription,
  type CurrentSubscriptionDiscount,
  type RevenueCatStore,
} from "../data/subscriptionPlans"

/** What may be found in `user.data.subscription`: the stored names plus the client's camelCase ones. */
export type StoredSubscription = Record<string, unknown>

function str(...values: unknown[]): string | undefined {
  for (const v of values) {
    if (typeof v === "string" && v !== "") return v
  }
  return undefined
}

function num(...values: unknown[]): number | undefined {
  for (const v of values) {
    if (typeof v === "number" && Number.isFinite(v)) return v
    if (typeof v === "string" && v.trim() !== "" && Number.isFinite(Number(v))) {
      return Number(v)
    }
  }
  return undefined
}

/** The stored `term` when it names a paid term; `free` is the placeholder of a plan without one. */
function storedTerm(value: unknown): PaymentTerm | undefined {
  if (
    value === PaymentTerm.Monthly ||
    value === PaymentTerm.Yearly ||
    value === PaymentTerm.Biennially
  ) {
    return value
  }
  return undefined
}

function mapDiscount(value: unknown): CurrentSubscriptionDiscount | undefined {
  if (!value || typeof value !== "object") return undefined
  const d = value as Record<string, unknown>
  const amount = num(d.amount)
  if (amount === undefined) return undefined
  return {
    amount,
    promotionCode: str(d.promotion_code, d.promotionCode),
    duration: d.duration === "forever" ? "forever" : "once",
  }
}

/** Term of a stored subscription: the stored `term` first, then the interval, then monthly. */
export function resolveStoredTerm(payment: StoredSubscription | undefined): PaymentTerm {
  if (!payment) return PaymentTerm.Monthly
  const fromTerm = storedTerm(payment.term)
  if (fromTerm) return fromTerm
  const interval = str(payment.interval)
  const count = num(payment.interval_count, payment.intervalCount) ?? 1
  if (interval === "year") {
    return count === 2 ? PaymentTerm.Biennially : PaymentTerm.Yearly
  }
  return PaymentTerm.Monthly
}

/** `validUntil` is the licence's expiry in Unix ms (the stored `expires` carries the grace period). */
export function mapStoredSubscription(
  payment: StoredSubscription | undefined,
  validUntil: number,
): CurrentSubscription {
  const p = payment ?? {}
  return {
    term: resolveStoredTerm(payment),
    plan: (str(p.plan) as PaymentPlan | undefined) ?? PaymentPlan.Standard,
    expires: validUntil.toString(),
    active: typeof p.active === "boolean" ? p.active : false,
    amount: num(p.amount) ?? 0,
    currency: str(p.currency) ?? "USD",
    interval: str(p.interval) ?? "month",
    intervalCount: num(p.interval_count, p.intervalCount) ?? 1,
    provider: (str(p.provider) as PaymentProvider | undefined) ?? PaymentProvider.None,
    priceId: str(p.price_id, p.priceId),
    paymentError:
      typeof p.payment_error === "boolean"
        ? p.payment_error
        : typeof p.paymentError === "boolean"
          ? p.paymentError
          : false,
    paymentErrorInfo: str(p.payment_error_info, p.paymentErrorInfo),
    subscriptionStatusInfo: str(p.subscription_status_info, p.subscriptionStatusInfo),
    discount: mapDiscount(p.discount),
    isProduction: typeof p.isProduction === "boolean" ? p.isProduction : undefined,
    subscriptionProvider: str(p.subscriptionProvider),
    subscriptionProviderId: str(
      p.subscriptionProviderID,
      p.subscriptionProviderId,
    ),
    store: str(p.store) as RevenueCatStore | undefined,
  }
}
